import { InterestProperty } from "@/modules/lead-page/types";
import { Box, Breadcrumbs, Divider, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import InterestActionDropdown from "./add-interest-action";
import ActivityHistory from "./activity-history";
import InterestActionInfo from "./interest-action-info";
import InfoCell from "@/modules/common/components/ui-kit/info-cell";

export default function InterestCard({
  interest,
}: {
  interest: InterestProperty;
}) {
  return (
    <Box
      sx={(t) => ({
        backgroundColor: t.nawy.colors.semantic.layer.primary,
        borderRadius: t.nawy.radius.xl,
        p: t.nawy.spacing.md,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "start",
        gap: t.nawy.spacing.sm,
      })}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          width: "100%",
        }}
      >
        {/* PROPERTY INFO */}
        <Box
          sx={(t) => ({
            display: "flex",
            flexDirection: "column",
            gap: t.nawy.spacing.xxs,
          })}
        >
          <Breadcrumbs
            separator="•"
            sx={(t) => ({
              color: t.nawy.colors.semantic.text.secondary,
            })}
          >
            <Box sx={(t) => ({ display: "flex", gap: t.nawy.spacing.xxs })}>
              <Typography
                sx={(t) => ({
                  ...t.nawy.typography.english.variants.body.sm.semibold,
                  color: t.nawy.colors.semantic.text.secondary,
                })}
              >
                Unit ID
              </Typography>
              <Typography
                sx={(t) => ({
                  ...t.nawy.typography.english.variants.body.sm.regular,
                  color: t.nawy.colors.semantic.text.secondary,
                })}
              >
                {interest.property.uid}
              </Typography>
            </Box>
            <Box sx={(t) => ({ display: "flex", gap: t.nawy.spacing.xxs })}>
              <Typography
                sx={(t) => ({
                  ...t.nawy.typography.english.variants.body.sm.semibold,
                  color: t.nawy.colors.semantic.text.secondary,
                })}
              >
                Unit Number
              </Typography>
              <Typography
                sx={(t) => ({
                  ...t.nawy.typography.english.variants.body.sm.regular,
                  color: t.nawy.colors.semantic.text.secondary,
                })}
              >
                {interest.property.unitId ?? "N/A"}
              </Typography>
            </Box>
          </Breadcrumbs>
          <Box
            sx={(t) => ({
              display: "flex",
              gap: t.nawy.spacing.xxs,
              alignItems: "center",
            })}
          >
            <Link
              to={`/property-tracks/${interest.track?.uid}`}
              target="_blank"
            >
              <Typography
                sx={(t) => ({
                  ...t.nawy.typography.english.variants.body.md.semibold,
                  color: t.nawy.colors.semantic.text.accent,
                })}
              >
                {interest.property.propertyType?.name} in{" "}
                {interest.property.compound?.name}
              </Typography>
            </Link>
            <Breadcrumbs
              separator="•"
              sx={(theme) => ({
                ...theme.nawy.typography.english.variants.body.md.regular,
                color: theme.nawy.colors.semantic.text.secondary,
              })}
            >
              <span>{interest.property.compound?.developer?.name}</span>
              <span>{interest.property.compound?.area?.name}</span>
            </Breadcrumbs>
          </Box>
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            EGP{" "}
            {interest.property.propertyPrice?.askingPrice?.toLocaleString() ||
              interest.property.propertyRental?.rentalPrice?.toLocaleString()}
          </Typography>
        </Box>
        {/* ACTIONS */}
        <Box
          sx={(t) => ({
            display: "flex",
            gap: t.nawy.spacing.xs,
            alignItems: "center",
          })}
        >
          <Box sx={{ width: "250px" }}>
            <InterestActionDropdown interest={interest} />
          </Box>
        </Box>
      </Box>
      {/* PROPERTY FACILITIES INFO */}
      <Box sx={{ width: "100%" }}>
        <Box
          sx={(t) => ({
            display: "flex",
            flexWrap: "wrap",
            gap: t.nawy.spacing.md,
            justifyContent: "start",
            width: "100%",
          })}
        >
          <InfoCell
            title="Total Area"
            description={
              interest.property.unitArea
                ? `${interest.property.unitArea} m²`
                : "N/A"
            }
          />
          <InfoCell
            title="Land Area"
            description={
              interest.property.landArea
                ? `${interest.property.landArea} m²`
                : "N/A"
            }
          />
          <InfoCell
            title="Garden Area"
            description={
              interest.property.gardenArea
                ? `${interest.property.gardenArea} m²`
                : "N/A"
            }
          />
          <InfoCell
            title="Bedrooms"
            description={
              interest.property.numberOfBedrooms
                ? `${interest.property.numberOfBedrooms}`
                : "N/A"
            }
          />
          <InfoCell
            title="Bathrooms"
            description={
              interest.property.numberOfBathrooms
                ? `${interest.property.numberOfBathrooms}`
                : "N/A"
            }
          />
          <InfoCell
            title="Floors"
            description={
              interest.property.numberOfFloors
                ? `${interest.property.numberOfFloors}`
                : "N/A"
            }
          />
        </Box>
      </Box>
      <Divider sx={{ width: "100%" }} />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "baseline",
        }}
      >
        {interest.lastAction && (
          <InterestActionInfo interestAction={interest.lastAction} />
        )}
        <ActivityHistory interest={interest} />
      </Box>
    </Box>
  );
}
